import { useCallback, useEffect, useState } from 'react';
import { Box, Heading, Icon, Image, Paragraph, Text } from '@zillow/constellation-10';
import { IconCheckmarkCircleFilled } from '@zillow/constellation-icons';
import { useMountedState } from 'react-use';

import documentImage from '../base64Images/documentBuild1x';
import { SECTION_CATEGORIES } from '../models/Section';

const PROGRESS_FADEOUT_TIME = 300;
const DEFAULT_DURATION = 3000;

type Props = {
  progressIndicatorDuration?: number;
  onLoad?(): void;
  label?: string;
  subLabel?: string;
};

export default function DocumentPreviewProgressIndicator({
  progressIndicatorDuration = DEFAULT_DURATION,
  onLoad,
  label = 'Hang tight',
  subLabel = 'We’re generating your lease document.',
}: Props) {
  const [progressIsFinished, setProgressIsFinished] = useState(false);
  const [progressTransitionIsFinished, setProgressTransitionIsFinished] = useState(false);
  const [stepInProgress, setStepInProgress] = useState(0);
  const isMounted = useMountedState();

  const steps = SECTION_CATEGORIES.map((category) => category.label);

  const onProgressStepperFinished = useCallback(() => {
    if (isMounted()) {
      setProgressIsFinished(true);
    }
  }, [isMounted]);

  const finishCurrentStep = useCallback(() => {
    if (stepInProgress === steps.length) {
      onProgressStepperFinished();
    } else if (isMounted()) {
      setStepInProgress(stepInProgress + 1);
    }
  }, [stepInProgress, steps.length, onProgressStepperFinished, isMounted]);

  const evaluateTransitionOut = useCallback(() => {
    if (progressIsFinished) {
      onLoad?.();

      setTimeout(() => {
        if (isMounted()) {
          setProgressTransitionIsFinished(true);
        }
      }, PROGRESS_FADEOUT_TIME);
    }
  }, [progressIsFinished, onLoad, isMounted]);

  useEffect(() => {
    finishCurrentStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTimeout(finishCurrentStep, progressIndicatorDuration / steps.length);
  }, [stepInProgress, finishCurrentStep, progressIndicatorDuration, steps.length]);

  useEffect(() => {
    if (progressIsFinished) {
      evaluateTransitionOut();
    }
  }, [progressIsFinished, evaluateTransitionOut]);

  return (
    <Box
      css={{
        position: 'absolute',
        top: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'white',
        transition: `${PROGRESS_FADEOUT_TIME}ms opacity`,
        width: '100%',
        height: '100%',
        paddingTop: '6rem',
        left: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
        opacity: progressIsFinished ? 0 : 1,
        zIndex: progressTransitionIsFinished ? -1 : 2,
      }}
    >
      <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
        <Image src={documentImage} alt="Document being generated" />
        <Heading level={1} css={{ textStyle: 'responsive.heading-md', marginBottom: 'tighter' }}>
          {label}
        </Heading>
        {subLabel && <Paragraph css={{ marginBottom: 'tight' }}>{subLabel}</Paragraph>}
        {steps.map((step: string, index: number) => (
          <Box key={index} css={{ marginBottom: 'tight', alignSelf: 'flex-start' }}>
            <Box
              css={{
                transition: 'opacity 500ms ease-in-out',
                opacity: index >= stepInProgress ? 0 : 1,
              }}
            >
              <Icon size="sm" css={{ marginRight: 'tighter', color: 'icon.accent.brand.hero' }}>
                <IconCheckmarkCircleFilled />
              </Icon>
              <Text>{step}</Text>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
